import { Injectable } from "@angular/core";
import { StorageKeys } from "../Dto/StorageKeys";

/**
 * API service for raw localStorage operations.
 * Handles direct browser localStorage access with type-safe keys.
 */
@Injectable({ providedIn: "root" })
export class StorageApi {
  /**
   * Get an item from localStorage by key.
   * Returns null if the key does not exist.
   */
  getItem(key: StorageKeys): string | null {
    return localStorage.getItem(key);
  }

  /**
   * Set an item in localStorage.
   * Value will be converted to string if not already a string.
   */
  setItem<T>(key: StorageKeys, value: T): void {
    const stringValue = (() => {
      if (typeof value === "string") {
        return value;
      }
      return JSON.stringify(value);
    })();
    localStorage.setItem(key, stringValue);
  }

  /**
   * Remove an item from localStorage by key.
   */
  removeItem(key: StorageKeys): void {
    localStorage.removeItem(key);
  }

  /**
   * Clear all items from localStorage.
   * Use with caution as this removes all data.
   */
  clear(): void {
    localStorage.clear();
  }

  /**
   * Check if an item exists in localStorage.
   */
  hasItem(key: StorageKeys): boolean {
    return localStorage.getItem(key) !== null;
  }

  /**
   * Get a parsed JSON value from localStorage.
   * Returns null if the key does not exist or parsing fails.
   */
  getJsonItem<T>(key: StorageKeys): T | null {
    const item = localStorage.getItem(key);
    if (!item) {
      return null;
    }

    try {
      return JSON.parse(item) as T;
    } catch {
      return null;
    }
  }

  /**
   * Set a JSON value in localStorage.
   * Value will be stringified before storage.
   */
  setJsonItem<T>(key: StorageKeys, value: T): void {
    localStorage.setItem(key, JSON.stringify(value));
  }
}
